import React, { useState, useEffect } from 'react';
import axiosInstance from '../../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaCar, FaTrash, FaCheck, FaTimes, FaEye, FaEdit, FaPlus, FaSearch, FaClipboardList, FaShareAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import '../../pages_premium.css';
import './CarsList.css';
import { getFrontendBaseUrl } from '../../utils/url';
import AdminShareModal from '../../components/common/AdminShareModal';
import AdminPagination from '../../components/common/AdminPagination';



const safeParseVerification = (verifVal) => {
  if (!verifVal) return null;
  if (typeof verifVal === 'object') return verifVal;
  try {
    const parsed = typeof verifVal === 'string' ? JSON.parse(verifVal) : verifVal;
    if (typeof parsed === 'object') return parsed;
  } catch (e) {
    if (typeof verifVal === 'string') return { info: verifVal };
  }
  return null;
};

const formatUserRole = (roleStr) => {
  if (!roleStr) return 'Lister';
  const r = roleStr.toLowerCase();
  if (r === 'agent' || r === 'lister') return 'Lister';
  if (r === 'owner' || r === 'business') return 'Business Owner';
  if (r === 'student') return 'Student';
  if (r === 'admin') return 'Admin';
  return 'Lister';
};

const getUserRoleBadgeClass = (roleStr) => {
  if (!roleStr) return 'badge-role-lister';
  const r = roleStr.toLowerCase();
  if (r === 'owner' || r === 'business') return 'badge-role-owner';
  if (r === 'student') return 'badge-role-student';
  if (r === 'admin') return 'badge-role-admin';
  return 'badge-role-lister';
};

const formatCategoryBadge = (cat) => {
  switch (cat) {
    case 'uber_eats':
      return { label: '🍔 Uber Eats', min: 'Min 1 Mo Contract', className: 'badge bg-success text-white' };
    case 'uber_taxi':
      return { label: '🚕 Uber / Taxi', min: 'Min 1 Mo Contract', className: 'badge bg-warning text-dark' };
    case 'pickup_van':
      return { label: '🛻 Pickup / Van', min: 'Min 1 Mo Contract', className: 'badge bg-primary text-white' };
    case 'per_day':
    default:
      return { label: '🚗 Per Day', min: 'Flexible (1-30+ Days)', className: 'badge bg-secondary text-white' };
  }
};

function CarsList() {
    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [shareItem, setShareItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const handleShareCar = (car) => {
        const frontendUrl = getFrontendBaseUrl();
        const images = Array.isArray(car.images) ? car.images : (typeof car.images === 'string' ? [car.images] : []);
        setShareItem({
            url: `${frontendUrl}/cars/${car.id}`,
            title: car.title || `${car.make || ''} ${car.model || ''}`,
            category: car.listing_type === 'sale' ? 'Car for Sale' : 'Car Rental',
            price: car.price ? `$${car.price}${car.listing_type === 'sale' ? '' : '/day'}` : '',
            location: car.location || '',
            image: images[0] || '',
            description: car.description || ''
        });
    };

    const fetchCars = async () => {
        try {
            const res = await axiosInstance.get('/cars/admin/all');
            setCars(res.data);
        } catch (error) {
            console.error('Error fetching cars:', error);
            toast.error('Failed to fetch cars');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCars();
    }, []);

    const deleteCar = async (id) => {
        if (!window.confirm('Are you sure you want to delete this car?')) return;
        try {
            await axiosInstance.delete(`/cars/${id}`);
            toast.success('Car deleted successfully');
            fetchCars();
        } catch (error) {
            console.error('Error deleting car:', error);
            toast.error('Failed to delete car');
        }
    };

    const filtered = cars.filter(c => {
        const matchesSearch = c.make?.toLowerCase().includes(search.toLowerCase()) ||
            c.model?.toLowerCase().includes(search.toLowerCase()) ||
            c.title?.toLowerCase().includes(search.toLowerCase()) ||
            c.location?.toLowerCase().includes(search.toLowerCase()) ||
            c.User?.name?.toLowerCase().includes(search.toLowerCase());
        if (!matchesSearch) return false;

        if (categoryFilter !== 'all') {
            const cat = c.car_category || 'per_day';
            if (cat !== categoryFilter) return false;
        }
        return true;
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [search, categoryFilter]);

    const paginatedCars = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="page-premium-wrapper cars-list-page">
            <div className="page-premium-content">
                
                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties">
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>
                    
                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaCar size={26} />
                        </div>
                        <div>
                            <h1 className="ph-title">Cars Management</h1>
                            <p className="ph-subtitle">Manage all listed vehicles (Rentals & Sales)</p>
                        </div>
                    </div>
                    
                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{cars.length}</span>
                            <span className="count-lbl">Total Cars</span>
                        </div>
                    </div>
                </div>

                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span> 
                            Listed Cars
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <div className="ptable-search">
                                <FaSearch className="ptable-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search cars by make, model, owner, role..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <Link to="/cars/add" className="btn-add-primary">
                                <FaPlus size={12} />
                                <span>Add New Car</span>
                            </Link>
                        </div>
                    </div>
                    
                    <div className="d-flex flex-wrap align-items-center gap-2 px-4 py-3 bg-light border-bottom">
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            🇦🇺 Category:
                        </span>
                        {[
                            { id: 'all', label: 'All Categories', count: cars.length },
                            { id: 'uber_eats', label: '🍔 Uber Eats', count: cars.filter(c => c.car_category === 'uber_eats').length },
                            { id: 'uber_taxi', label: '🚕 Uber / Taxi', count: cars.filter(c => c.car_category === 'uber_taxi').length },
                            { id: 'pickup_van', label: '🛻 Pickup / Van', count: cars.filter(c => c.car_category === 'pickup_van').length },
                            { id: 'per_day', label: '🚗 Per Day', count: cars.filter(c => !c.car_category || c.car_category === 'per_day').length }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setCategoryFilter(tab.id)}
                                className={`btn btn-sm ${categoryFilter === tab.id ? 'btn-dark shadow-sm' : 'btn-outline-secondary bg-white'}`}
                                style={{ borderRadius: '8px', fontSize: '12px', fontWeight: 600, padding: '4px 10px' }}
                            >
                                {tab.label} <span className="badge badge-light border ml-1" style={{ fontSize: '10px' }}>{tab.count}</span>
                            </button>
                        ))}
                    </div>
                    
                    <div className="ptable-wrap">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '50px' }}>#</th>
                                    <th style={{ minWidth: '150px' }}>Owner / User</th>
                                    <th style={{ minWidth: '130px' }}>User Role</th>
                                    <th style={{ minWidth: '110px' }}>Listing Type</th>
                                    <th style={{ minWidth: '150px' }}>Category (AU)</th>
                                    <th style={{ minWidth: '240px' }}>Car & Specifications</th>
                                    <th style={{ minWidth: '160px' }}>Verification Details</th>
                                    <th style={{ minWidth: '140px' }}>Location</th>
                                    <th style={{ minWidth: '130px' }}>Price & Period</th>
                                    <th style={{ minWidth: '110px' }}>Status</th>
                                    <th className="text-center" style={{ minWidth: '110px' }}>Total Requests</th>
                                    <th className="text-center" style={{ minWidth: '150px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedCars.map((car, idx) => {
                                    const isSelling = car.listing_type === 'selling' || car.listing_type === 'sale' || car.type === 'selling';
                                    const rawRole = car.listing_role || car.User?.role;
                                    const verif = safeParseVerification(car.verification);
                                    const reqCount = car.total_requests || 0;
                                    
                                    return (
                                        <tr key={car.id}>
                                            <td style={{ color: '#9ba6c0', fontWeight: 600 }}>{(currentPage - 1) * pageSize + idx + 1}</td>
                                            <td>
                                                {car.User ? (
                                                    <div className="d-flex flex-column">
                                                        <strong style={{ color: '#0f172a' }}>{car.User.name}</strong>
                                                        <span style={{ fontSize: '12px', color: '#64748b' }}>{car.User.email}</span>
                                                    </div>
                                                ) : (
                                                    <span style={{ color: '#94a3b8' }}>Admin / Guest</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`badge ${getUserRoleBadgeClass(rawRole)} px-2.5 py-1 text-capitalize`} style={{ fontSize: '12px', fontWeight: 600 }}>
                                                    {formatUserRole(rawRole)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`badge ${isSelling ? 'badge-type-selling' : 'badge-type-rental'} px-2.5 py-1 text-capitalize`} style={{ fontSize: '12px', fontWeight: 600 }}>
                                                    {isSelling ? '🏷️ Selling' : '🚗 Rental'}
                                                </span>
                                            </td>
                                            <td>
                                                {(() => {
                                                    const catInfo = formatCategoryBadge(car.car_category);
                                                    return (
                                                        <div className="d-flex flex-column gap-1">
                                                            <span className={`${catInfo.className} px-2 py-1 text-nowrap`} style={{ fontSize: '11px', fontWeight: 600 }}>
                                                                {catInfo.label}
                                                            </span>
                                                            <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 500 }}>
                                                                {catInfo.min}
                                                            </span>
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <strong style={{ fontSize: '0.9rem', color: '#0f172a' }}>{car.title || `${car.make} ${car.model}`}</strong>
                                                    <span style={{ fontSize: '12px', color: '#64748b' }} className="mt-0.5">
                                                        {car.make} {car.model} {car.variant ? `(${car.variant})` : ''} {car.year ? `- ${car.year}` : ''}
                                                    </span>
                                                    <div className="mt-1.5 d-flex flex-wrap gap-1 align-items-center">
                                                        {car.transmission && <span className="badge badge-light border text-muted px-2 py-0.5 small">{car.transmission}</span>}
                                                        {car.fuelType && <span className="badge badge-light border text-muted px-2 py-0.5 small">{car.fuelType}</span>}
                                                        {car.seats && <span className="badge badge-light border text-muted px-2 py-0.5 small">{car.seats} Seats</span>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {!verif ? (
                                                    <span className="badge badge-light text-muted border px-2 py-1" style={{ fontSize: '11px' }}>Not Provided</span>
                                                ) : (
                                                    <div className="d-flex flex-column gap-1" style={{ fontSize: '11px' }}>
                                                        {verif.regoNumber && (
                                                            <span className="badge badge-light border text-dark text-left px-2 py-1">
                                                                <strong>Rego:</strong> {verif.regoNumber} {verif.regoState ? `(${verif.regoState})` : ''}
                                                            </span>
                                                        )}
                                                        {verif.vin && (
                                                            <span className="badge badge-light border text-dark text-left px-2 py-1">
                                                                <strong>VIN:</strong> {verif.vin}
                                                            </span>
                                                        )}
                                                        {verif.insuranceProvider && (
                                                            <span className="badge badge-light border text-dark text-left px-2 py-1">
                                                                <strong>Ins:</strong> {verif.insuranceProvider}
                                                            </span>
                                                        )}
                                                        {!verif.regoNumber && !verif.vin && !verif.insuranceProvider && (
                                                            <span className="badge badge-light text-muted border px-2 py-1">
                                                                {typeof verif === 'string' ? verif : 'Custom Verified'}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <span style={{ color: '#475569', fontWeight: 500 }}>{car.location || car.city || 'N/A'}</span>
                                            </td>
                                            <td>
                                                <span className="price-badge">
                                                    ${car.price} {isSelling ? '(Sale Price)' : `/ ${car.period || 'day'}`}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`ptag ${car.status === 'available' ? 'ptag-active' : 'ptag-banned'}`}>
                                                    {car.status === 'available' ? <FaCheck style={{ marginRight: 4 }} /> : <FaTimes style={{ marginRight: 4 }} />}
                                                    {car.status || 'Available'}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <Link
                                                    to={`/car-requests?carId=${car.id}`}
                                                    className="badge px-3 py-1.5 text-decoration-none"
                                                    style={{
                                                        backgroundColor: reqCount > 0 ? '#eff6ff' : '#f8fafc',
                                                        color: reqCount > 0 ? '#2563eb' : '#64748b',
                                                        border: `1px solid ${reqCount > 0 ? '#bfdbfe' : '#e2e8f0'}`,
                                                        fontSize: '12px',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    📋 {reqCount} {reqCount === 1 ? 'Request' : 'Requests'}
                                                </Link>
                                            </td>
                                            <td className="text-center">
                                                <div className="car-actions d-flex justify-content-center gap-1">
                                                    <button
                                                        type="button"
                                                        className="action-btn action-btn-share"
                                                        onClick={() => handleShareCar(car)}
                                                        title="Share Car Listing"
                                                    >
                                                        <FaShareAlt />
                                                    </button>
                                                    <Link 
                                                        to={`/car-requests?carId=${car.id}`}
                                                        className="action-btn"
                                                        style={{ backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}
                                                        title="View All Requests"
                                                    >
                                                        <FaClipboardList />
                                                    </Link>
                                                    <Link 
                                                        to={`/cars/${car.id}`}
                                                        className="action-btn action-btn-view"
                                                        title="View Full Details"
                                                    >
                                                        <FaEye />
                                                    </Link>
                                                    <Link 
                                                        to={`/cars/edit/${car.id}`}
                                                        className="action-btn action-btn-edit"
                                                        title="Edit Car"
                                                    >
                                                        <FaEdit />
                                                    </Link>
                                                    <button 
                                                        type="button"
                                                        className="action-btn action-btn-delete"
                                                        onClick={() => deleteCar(car.id)}
                                                        title="Delete Car"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan="11">
                                            <div className="ptable-empty">
                                                <div className="ptable-empty-icon">🚗</div>
                                                <p>No listed cars found.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <AdminPagination
                        totalItems={filtered.length}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(newSize) => { setPageSize(newSize); setCurrentPage(1); }}
                        itemLabel="cars"
                    />
                </div>

            </div>

            <AdminShareModal
                isOpen={!!shareItem}
                onClose={() => setShareItem(null)}
                {...shareItem}
            />
        </div>
    );
}

export default CarsList;
